import { ChangeEvent, useEffect, useState, useRef } from "react";
import dayjs from 'dayjs';
import { DateFormat, InputStyle, MergeWithOverwrite, ValidationErrorStyle } from "src/common";
import { errorA11yProps, errorIdFor } from "src/common/style/a11y.style";
import { InputLabel } from "src/components";


const findAddedCharacter = (originalString: string, newString: string) => {
  const minLength = Math.min(originalString.length, newString.length);

  // Pronađi prvu poziciju na kojoj se stringovi razlikuju
  let diffIndex = -1;
  for (let i = 0; i < minLength; i++) {
    if (originalString[i] !== newString[i]) {
      // Provjeri je li znak na trenutnoj poziciji u newString '/'
      // i je li znak na prethodnoj poziciji u newString također '/'
      if (newString[i] === '/' && newString[i - 1] === '/') {
        // Ako jest, pretpostavimo da je dodan '/' na pogrešno mjesto
        diffIndex = i - 1;
      } else {
        diffIndex = i;
      }
      break;
    }
  }

  // Ako nema razlike u prvih minLength znakova, dodani znak je na kraju novog stringa
  if (diffIndex === -1) {
    return {
      position: newString.length - 1,
      character: newString[newString.length - 1]
    };
  }
  // Ako je pronađena razlika, dodani znak je na poziciji diffIndex u novom stringu
  return {
    position: diffIndex,
    character: newString[diffIndex]
  };
}

// Helper function to get days in month
const getDaysInMonth = (month: number, year: number): number => {
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  // Check if it's a leap year (February)
  if (month === 2) {
    // Leap year: year is divisible by 4, but not by 100, or divisible by 400
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      return 29;
    }
  }
  
  return daysInMonth[month - 1] || 31;
};

// Helper function to validate and adjust date
const validateAndAdjustDate = (day: string, month: string, year: string, canBeLargeThanCurrentYear?: boolean): { day: string, month: string, year: string } => {
  let adjustedDay = day;
  let adjustedMonth = month;
  let adjustedYear = year;
  
  // Only validate year if it has 4 digits
  if (year && year.length === 4) {
    const yearNum = parseInt(year);
    if (yearNum < 1900 || (yearNum > new Date().getFullYear() && !canBeLargeThanCurrentYear)) {
      adjustedYear = '';
    }
  }
  
  // Validate month if it has 2 digits (1-12)
  if (month && month.length === 2) {
    const monthNum = parseInt(month);
    if (monthNum < 1 || monthNum > 12) {
      adjustedMonth = '';
    }
  }
  
  // Validate day if it has 2 digits (1-31)
  if (day && day.length === 2) {
    const dayNum = parseInt(day);
    if (dayNum < 1 || dayNum > 31) {
      adjustedDay = '';
    }
  }
  
  // Check and adjust day based on month (even without year)
  if (day && day.length === 2 && month && month.length === 2) {
    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    
    // Use current year for validation if year is not provided
    const yearToUse = year && year.length === 4 ? parseInt(year) : canBeLargeThanCurrentYear ? new Date().getFullYear() : new Date().getFullYear();
    const maxDays = getDaysInMonth(monthNum, yearToUse);
    
    // Special case: February 29th in non-leap years
    if (monthNum === 2 && dayNum === 29 && year && year.length === 4) {
      const yearNum = parseInt(year);
      const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
      if (!isLeapYear) {
        adjustedDay = '28'; // Switch to February 28th for non-leap years
      }
    } else if (dayNum > maxDays) {
      adjustedDay = maxDays.toString().padStart(2, '0');
    }
  }
  
  return { day: adjustedDay, month: adjustedMonth, year: adjustedYear };
};

export type InputBirthDateProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  value?: string;
  error?: string;
  canBeLargeThanCurrentYear?: boolean;
  onChange?: (value: string) => void;
}
>;

// format input-a DD/MM/YYYY
// format output-a / value YYYY-MM-DD

export const InputBirthDate: React.FC<InputBirthDateProps> = ({
  label,
  value = "",
  error = '',
  canBeLargeThanCurrentYear = false,
  onChange,
  ...props
}) => {
  const formatToInputValue = (originalValue: string): string => {
    const originalValueFormatted = originalValue ? dayjs(originalValue).format(DateFormat) : '';
    const [year, month, day] = originalValueFormatted.split('-');
    const formattedDate = `${day ? `${day}/` : ''}${month ? `${month}/` : ''}${year ?? ''}`;
    return formattedDate;
  };

  const [inputValue, setInputValue] = useState(() => formatToInputValue(value));
  const externalValueRef = useRef(value);
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    // Only update if this is an external value change (not from user input)
    if (!isInternalUpdateRef.current && externalValueRef.current !== value) {
      const formattedValue = formatToInputValue(value);
      setInputValue(formattedValue);
      externalValueRef.current = value;
    }
  }, [value]);

  const formatToOutputValue = (originalValue: string): string => {
    const [day, month, year] = originalValue.split('/');
    // Pad year to 4 digits so "77" becomes "0077" (year 77, not 1977)
    const paddedYear = year ? year.padStart(4, '0') : '';
    const formattedDate = `${paddedYear ? `${paddedYear}-` : ''}${month ? `${month}-` : ''}${day ?? ''}`;
    return formattedDate;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^\d/]/g, ""); // Remove all non-numeric characters except '/'

    // Ensure input length does not exceed 10 characters (DD/MM/YYYY)
    if (input.length > 10) {
      input = input.slice(0, 10);
    }

    // Add slashes at the correct positions
    if (input.length > inputValue.length) {
      const addedChar = findAddedCharacter(inputValue, input);
      if (addedChar.character === '/' && (addedChar.position !== 2 && addedChar.position !== 5)) {
        return; // Do nothing if '/' is at a wrong position
      }
      const [day] = input.split('/');
      if (input.length > 2 && day?.length && day.length >= 2 && input[2] !== '/') {
        input = `${input.slice(0, 2)}/${input.slice(2)}`;
      }
      const [day2, month2] = input.split('/');
      if (input.length > 5 && month2?.length && month2.length >= 2 && day2?.length && day2.length >= 2 && input[5] !== '/') {
        input = `${input.slice(0, 5)}/${input.slice(5)}`;
      }
    } else {
      // Remove slashes when the input length decreases
      if (input.length === 3 && input[2] === '/') {
        input = input.slice(0, 2);
      }
      if (input.length === 6 && input[5] === '/') {
        input = input.slice(0, 5);
      }
    }

    // Apply date validation and adjustment
    const [day, month, year] = input.split('/');
    const validatedDate = validateAndAdjustDate(day || '', month || '', year || '', canBeLargeThanCurrentYear);
    
    // Reconstruct the input with validated values
    let validatedInput = '';
    if (validatedDate.day) {
      validatedInput += validatedDate.day;
      if (validatedDate.month) {
        validatedInput += '/' + validatedDate.month;
        if (validatedDate.year) {
          validatedInput += '/' + validatedDate.year;
        }
      }
    }

    setInputValue(validatedInput);
    
    // Mark this as an internal update to prevent useEffect from running
    isInternalUpdateRef.current = true;
    if (onChange) onChange(formatToOutputValue(validatedInput));
    // Reset the flag after the current render cycle
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }

  return (
    <div>
      {label ? <InputLabel htmlFor={props.id} disabled={props.disabled}>{label}</InputLabel> : null}
      <InputStyle
        autoComplete="off"
        {...props}
        type='tel'
        id={props.id}
        {...errorA11yProps(props.id, error)}
        value={inputValue}
        onChange={handleChange}
        hasError={!!error}
        placeholder="DD/MM/GGGG"
        maxLength={10}
      />
      {error ? <ValidationErrorStyle id={errorIdFor(props.id)}>{error}</ValidationErrorStyle> : null}
    </div>
  );
};
